import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { browserHistory, withRouter } from 'react-router';
import { getTranslatedString, getTranslatedName } from 'helpers/i18n';
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import { getProperties } from 'actions/landlordPropertiesActions';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import { AccentColor } from 'helpers/AccentColorHelper';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import AntDrawer from 'components/ui/Drawer';
import BatchCreating from './BatchCreating';
import MediaQuery from 'react-responsive';
import StatusTag from 'components/ui/StatusTag';
import { getFilteredBatches, getBatchStats } from '../store/actions';
import { featureEnabled } from 'helpers/ToggleBot';
import { media } from 'helpers/MediaHelper';

const RestyledHeaderContainer = styled(StyledHeaderContainer)`
  ${media.mobile`
    top: -82px;
  `};
  ${media.mobileSmall`
    top: -128px;
  `};
`;

class BatchesPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: { search: '', properties: [] },
      filtersActive: false,
      currentTab: 'draft',
      sortKey: '',
      sortOrder: '',
      offset: 0,
      createBatchDrawerOpen: false,
    };
  }

  componentDidMount() {
    this.props.getProperties({ for_multiselect: true, show_cities: true });
    this.fetchFilteredBatches();
  }

  handleTabChange = (tabName) => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => {
        this.fetchFilteredBatches();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key === 'original_id' ? 'id' : key === 'total_amount' ? 'total_amount_cents' : key,
        sortOrder: order,
        offset: 0,
      },
      () => {
        this.fetchFilteredBatches();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleClearFilters = () => {
    this.setState(
      {
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
        },
        filtersActive: false,
        currentTab: 'draft',
        sortKey: '',
        sortOrder: '',
        offset: 0,
      },
      () => {
        this.fetchFilteredBatches();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleSubmitFilters = (values) => {
    const isFiltersUntouched =
      values.search === '' && Array.isArray(values.properties) && !values.properties.includes(true);
    this.setState(
      {
        filtersActive: !isFiltersUntouched,
        filters: { search: values.search, properties: values.properties },
        offset: 0,
      },
      () => {
        this.fetchFilteredBatches();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  fetchFilteredBatches = async (ignoreOffset = false) => {
    const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

    let fetchPromise = null;

    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    const filterArray = (array, booleanArrayMask) => {
      return array.filter((_, i) => booleanArrayMask[i]);
    };

    if (filtersActive) {
      const propertiesToFilter = filterArray(this.props.properties, filters.properties).map(
        (data) => data.value
      );

      filterOptions = {
        query: filters.search,
        property_ids: propertiesToFilter,
        sort_key: sortKey,
        sort_order: sortOrder,
      };

      fetchPromise = this.props.getFilteredBatches('all', ignoreOffset ? 0 : offset, filterOptions);
    } else {
      fetchPromise = this.props.getFilteredBatches(
        currentTab,
        ignoreOffset ? 0 : offset,
        filterOptions
      );
    }

    this.props.getBatchStats();
    await fetchPromise;
    const self = this;
    this.setState({ offset: self.props.batches.length });
  };

  closeCreateBatchDrawer = () => {
    this.setState({
      createBatchDrawerOpen: false,
    });
  };

  openCreateBatchDrawer = () => {
    this.setState({
      createBatchDrawerOpen: true,
    });
  };

  render() {
    const tabs = [
      {
        title: getTranslatedString('landlordPortal.batches.draft'),
        label: getTranslatedString('landlordPortal.batches.draft'),
        value: 'draft',
        count: this.props.batchStats.draft,
        onClick: () => this.handleTabChange('draft'),
      },
      {
        title: getTranslatedString('landlordPortal.batches.scheduled'),
        label: getTranslatedString('landlordPortal.batches.scheduled'),
        value: 'scheduled',
        count: this.props.batchStats.scheduled,
        onClick: () => this.handleTabChange('scheduled'),
      },
      {
        title: getTranslatedString('landlordPortal.batches.processing'),
        label: getTranslatedString('landlordPortal.batches.processing'),
        value: 'processing',
        count: this.props.batchStats.processing,
        onClick: () => this.handleTabChange('processing'),
      },
      {
        title: getTranslatedString('landlordPortal.batches.processed'),
        label: getTranslatedString('landlordPortal.batches.processed'),
        value: 'processed',
        count: this.props.batchStats.processed,
        onClick: () => this.handleTabChange('processed'),
      },
    ];

    let columns = [
      {
        title: getTranslatedString('landlordPortal.batches.status'),
        dataKey: 'status',
        sortable: false,
        render: ({ data }) => <StatusTag status={data} />,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.batchName'),
        dataKey: 'name',
        render: ({ data }) => getTranslatedName(data),
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.batchId'),
        dataKey: 'original_id',
        sortable: true,
        width: 150,
      },
      {
        title: getTranslatedString('landlordPortal.batches.payments'),
        dataKey: 'payments_count',
        sortable: false,
        width: 150,
      },
      {
        title: getTranslatedString('landlordPortal.batches.scheduledDate'),
        dataKey: 'post_at',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.totalBalance'),
        dataKey: 'total_amount',
        sortable: true,
        width: 300,
      },
    ];

    if (featureEnabled('payments.merchant_subaccount')) {
      columns.splice(1, 0, {
        title: getTranslatedString('landlordPortal.batches.property'),
        dataKey: 'property_name',
        sortable: true,
        width: 300,
      });
      columns.splice(2, 0, {
        title: getTranslatedString('landlordPortal.batches.account'),
        dataKey: 'merchant_subaccount_name',
        sortable: true,
        width: 300,
      });
    }

    const filterOptions = [
      {
        useSearch: true,
        options: this.props.properties.map((data) => data.label),
        name: 'properties',
        title: getTranslatedString('landlordPortal.rentRoll.selectPropertiesToFilter'),
      },
    ];

    let groupBy = 'property_name';
    if (featureEnabled('ui.properties.namespace_by_city')) {
      groupBy = 'property_name_city';
    }
    if (featureEnabled('payments.merchant_subaccount')) {
      groupBy = 'merchant_subaccount_name';
    }

    return (
      <StyledMain>
        <RestyledHeaderContainer>
          <StyledHeader>
            <HeaderWithPeriod
              headerText={getTranslatedString('landlordPortal.batches.paymentBatches')}
              newStyle
            />
            <StyledAddButton accentColor={AccentColor()} onClick={this.openCreateBatchDrawer}>
              {getTranslatedString('landlordPortal.batches.createNew')}
            </StyledAddButton>
          </StyledHeader>
          <TableFilters
            filters={this.state.filters}
            handleSubmitFilters={this.handleSubmitFilters}
            handleClearFilters={this.handleClearFilters}
            filterOptions={filterOptions}
            accentColor={AccentColor()}
          />
        </RestyledHeaderContainer>

        <StyledTableContainer>
          <MediaQuery query="(min-device-width: 750px)">
            <TabbedTable
              setRef={(ref) => {
                this.table = ref;
              }}
              accentColor={AccentColor()}
              groupRowsBy={groupBy}
              tabs={tabs}
              columns={columns}
              data={this.props.batches}
              loadMoreData={this.fetchFilteredBatches}
              filtersActive={this.state.filtersActive}
              onRowClick={(data) => {
                if (data.original_id) {
                  browserHistory.push('/companies/batches/' + data.original_id);
                }
              }}
              onColumnClick={(data) => this.handleSort(data.key, data.order)}
              searchTotalResultsCount={this.props.searchResultCount}
            />
          </MediaQuery>
          <MediaQuery query="(max-device-width: 750px)">
            <MobileTable
              groupRowsBy={groupBy}
              data={this.props.batches}
              tabs={tabs}
              tabIndex={this.state.currentTab}
              tabChange={this.handleTabChange}
              columns={columns}
              title="subject"
              tag="status"
              stickyHeaderTop={'107px'}
              rowHeader="name"
              fieldsAboveFold={[
                {
                  title: getTranslatedString('landlordPortal.batches.batchId'),
                  key: 'id',
                },
                {
                  title: getTranslatedString('landlordPortal.batches.totalBalance'),
                  key: 'total_amount',
                },
              ]}
              fieldsBelowFold={[
                {
                  title: getTranslatedString('landlordPortal.batches.scheduledDate'),
                  key: 'post_at',
                },
                {
                  title: getTranslatedString('landlordPortal.batches.payments'),
                  key: 'payments_count',
                },
              ]}
              onRowClick={(data) => {
                if (data.id) {
                  browserHistory.push('/companies/batches/' + data.id);
                }
              }}
            />
          </MediaQuery>
        </StyledTableContainer>

        <AntDrawer
          maskClosable={false}
          keyboard
          visible={this.state.createBatchDrawerOpen}
          onClose={this.closeCreateBatchDrawer}
          closable={false}
          width={600}
        >
          {this.state.createBatchDrawerOpen && (
            <BatchCreating
              handleClose={this.closeCreateBatchDrawer}
              onSubmissionComplete={() => this.fetchFilteredBatches({ ignoreOffset: true })}
            />
          )}
        </AntDrawer>
      </StyledMain>
    );
  }
}

const mapStateToProps = (store) => ({
  batches: store.landlord.batches.batches,
  searchResultCount: store.landlord.batches.searchResultCount,
  properties: store.landlordProperties.properties,
  batchStats: store.landlord.batches.batchStats,
});

const actionCreators = {
  getFilteredBatches,
  getProperties,
  getBatchStats,
};

export default connect(mapStateToProps, actionCreators)(withRouter(BatchesPage));
